import { useMemo } from "react";
import { Box, GlobalStyles, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridReadyEvent,
	ICellRendererParams,
	RowClassParams,
	RowClickedEvent,
	RowStyle,
} from "ag-grid-community";
import type { MonthlyEstimation } from "../models/MonthlyEstimation";

const CENTER_CELL = { textAlign: "center" } as const;
const RIGHT_CELL = { textAlign: "right" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const tonsFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const formatTons = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return tonsFormatter.format(n);
};

const buildColumnDefs = (
	onAxiomFire: (month: string) => void,
	isFetchingAxiom: boolean,
): ColDef<MonthlyEstimation>[] => [
	{
		field: "Name",
		headerName: "Month",
		flex: 1,
		minWidth: 90,
		sortable: false,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ForecastedProduction",
		headerName: "Estimated",
		flex: 1,
		minWidth: 100,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatTons(p.value),
	},
	{
		field: "ActualProduction",
		headerName: "Actual",
		flex: 1,
		minWidth: 100,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatTons(p.value),
	},
	{
		headerName: "",
		colId: "axiom",
		width: 48,
		sortable: false,
		filter: false,
		resizable: false,
		cellStyle: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		},
		cellRenderer: (p: ICellRendererParams<MonthlyEstimation>) => {
			const isFuture = p.data?.Forecast === 1;
			return (
				<IconButton
					size="small"
					color="primary"
					disabled={isFetchingAxiom || isFuture}
					onClick={(e) => {
						e.stopPropagation();
						if (p.data) onAxiomFire(p.data.Month);
					}}
					title={
						isFuture
							? "Future month — no Actual data in Axiom yet"
							: "Auto-populate Actual from Axiom (KPI scan tons for this month). Estimated stays untouched."
					}
					sx={{ p: 0.5 }}
				>
					<DownloadIcon fontSize="small" />
				</IconButton>
			);
		},
	},
];

interface Props {
	rows: MonthlyEstimation[];
	selectedMonth: string | null;
	isFetchingAxiom: boolean;
	onRowClicked: (row: MonthlyEstimation) => void;
	onAxiomFire: (month: string) => void;
	onGridReady: (event: GridReadyEvent<MonthlyEstimation>) => void;
}

export const ForecastDataEntryTable = ({
	rows,
	selectedMonth,
	isFetchingAxiom,
	onRowClicked,
	onAxiomFire,
	onGridReady,
}: Props) => {
	const columnDefs = useMemo(
		() => buildColumnDefs(onAxiomFire, isFetchingAxiom),
		[onAxiomFire, isFetchingAxiom],
	);

	const getRowStyle = (
		params: RowClassParams<MonthlyEstimation>,
	): RowStyle | undefined => {
		if (params.data?.Month === selectedMonth) {
			return { backgroundColor: "rgba(99, 102, 241, 0.12)" } as RowStyle;
		}
		if (params.data?.Forecast === 1) {
			return { color: "#94a3b8", fontStyle: "italic" } as RowStyle;
		}
		return undefined;
	};

	const handleRowClicked = (e: RowClickedEvent<MonthlyEstimation>) => {
		if (e.data) onRowClicked(e.data);
	};

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<AgGridReact<MonthlyEstimation>
				columnDefs={columnDefs}
				rowData={rows}
				rowModelType="clientSide"
				defaultColDef={{ resizable: true }}
				autoSizeStrategy={{ type: "fitGridWidth" }}
				suppressContextMenu
				headerHeight={32}
				rowHeight={32}
				onGridReady={onGridReady}
				onRowClicked={handleRowClicked}
				getRowStyle={getRowStyle}
				getRowId={(p) => String(p.data.Month)}
			/>
		</Box>
	);
};
